import type { ILlm } from '../llms/llms-store.svelte'
import { DatasetMetadataType } from '@routes/data-qa/datasets/dataset-metadata-type'
import { ILabelingInfo } from '@routes/data-qa/datasets/labeling-info'
import { HirundoCSV } from '@utils/hirundo-csv.svelte'

enum UnlearningRunStatus {
  PENDING = 'PENDING',
  RECEIVED = 'RECEIVED',
  STARTED = 'STARTED',
  SUCCESS = 'SUCCESS',
  FAILURE = 'FAILURE',
  RETRY = 'RETRY',
  REVOKED = 'REVOKED',
  IGNORED = 'IGNORED',
  REJECTED = 'REJECTED',
  AWAITING_MANUAL_APPROVAL = 'AWAITING_MANUAL_APPROVAL',
}

const displayRunStatus = {
  [UnlearningRunStatus.PENDING]: 'Pending',
  [UnlearningRunStatus.STARTED]: 'Started',
  [UnlearningRunStatus.SUCCESS]: 'Success',
  [UnlearningRunStatus.FAILURE]: 'Failure',
  [UnlearningRunStatus.RETRY]: 'Retry',
  [UnlearningRunStatus.REVOKED]: 'Cancelled',
  [UnlearningRunStatus.REJECTED]: 'Rejected',
  [UnlearningRunStatus.AWAITING_MANUAL_APPROVAL]: 'Awaiting approval',
}
const displayRunStatusSubtitle: Record<UnlearningRunStatus, string> = {
  [UnlearningRunStatus.RECEIVED]: 'Run request received',
  [UnlearningRunStatus.IGNORED]: 'Run ignored',
  [UnlearningRunStatus.PENDING]: 'Run not yet started: Provisioning compute',
  [UnlearningRunStatus.STARTED]: 'Run in progress',
  [UnlearningRunStatus.SUCCESS]: '',
  [UnlearningRunStatus.FAILURE]: 'Run failed. Please check your dataset',
  [UnlearningRunStatus.RETRY]: 'Run retrying',
  [UnlearningRunStatus.REVOKED]: 'Run cancelled',
  [UnlearningRunStatus.REJECTED]: 'Run rejected. Try re-running',
  [UnlearningRunStatus.AWAITING_MANUAL_APPROVAL]: 'Run awaiting approval',
}

const getRunStatusSubtitle = (run: $state.Snapshot<IUnlearningRun>) => {
  if (
    run.errorReason &&
    [UnlearningRunStatus.FAILURE, UnlearningRunStatus.REJECTED].includes(run.status)
  ) {
    return run.errorReason
  }
  return displayRunStatusSubtitle[run.status]
}

enum BehaviorType {
  BIAS = 'BIAS',
  HALLUCINATION = 'HALLUCINATION',
  SECURITY = 'SECURITY',
  REFUSAL = 'REFUSAL',
  CUSTOM = 'CUSTOM',
}

enum BiasType {
  ALL = 'ALL',
  RACE = 'RACE',
  NATIONALITY = 'NATIONALITY',
  GENDER = 'GENDER',
  PHYSICAL_APPEARANCE = 'PHYSICAL_APPEARANCE',
  RELIGION = 'RELIGION',
  AGE = 'AGE',
}

const biasTypeValues = [
  BiasType.ALL,
  BiasType.RACE,
  BiasType.NATIONALITY,
  BiasType.GENDER,
  BiasType.PHYSICAL_APPEARANCE,
  BiasType.RELIGION,
  BiasType.AGE,
]

const behaviorTypeValues = [
  BehaviorType.BIAS,
  BehaviorType.SECURITY,
  BehaviorType.REFUSAL,
  BehaviorType.CUSTOM,
]
// TODO: Add hallucination to behaviorTypeValues when supported

abstract class BehaviorBase {
  abstract type: BehaviorType
}

abstract class Bias extends BehaviorBase {
  public type: BehaviorType.BIAS = BehaviorType.BIAS
}

class AllBias extends Bias {
  public biasType: BiasType.ALL = BiasType.ALL
}

class RaceBias extends Bias {
  public biasType: BiasType.RACE = BiasType.RACE
}

class NationalityBias extends Bias {
  public biasType: BiasType.NATIONALITY = BiasType.NATIONALITY
}

class GenderBias extends Bias {
  public biasType: BiasType.GENDER = BiasType.GENDER
}

class PhysicalAppearanceBias extends Bias {
  public biasType: BiasType.PHYSICAL_APPEARANCE = BiasType.PHYSICAL_APPEARANCE
}

class ReligionBias extends Bias {
  public biasType: BiasType.RELIGION = BiasType.RELIGION
}

class AgeBias extends Bias {
  public biasType: BiasType.AGE = BiasType.AGE
}

enum HallucinationType {
  GENERAL = 'GENERAL',
  MEDICAL = 'MEDICAL',
  LEGAL = 'LEGAL',
  DEFENSE = 'DEFENSE',
}

const hallucinationTypeValues = [
  HallucinationType.GENERAL,
  HallucinationType.MEDICAL,
  HallucinationType.LEGAL,
  HallucinationType.DEFENSE,
]

const hallucinationTypeLabels = {
  [HallucinationType.GENERAL]: 'General',
  [HallucinationType.MEDICAL]: 'Medical',
  [HallucinationType.LEGAL]: 'Legal',
  [HallucinationType.DEFENSE]: 'Defense',
}

abstract class HallucinationsBehavior extends BehaviorBase {
  public type: BehaviorType.HALLUCINATION = BehaviorType.HALLUCINATION
}

class GeneralHallucinationsBehavior extends HallucinationsBehavior {
  public hallucinationType: HallucinationType.GENERAL = HallucinationType.GENERAL
}

class MedicalHallucinationsBehavior extends HallucinationsBehavior {
  public hallucinationType: HallucinationType.MEDICAL = HallucinationType.MEDICAL
}

class LegalHallucinationsBehavior extends HallucinationsBehavior {
  public hallucinationType: HallucinationType.LEGAL = HallucinationType.LEGAL
}

class DefenseHallucinationsBehavior extends HallucinationsBehavior {
  public hallucinationType: HallucinationType.DEFENSE = HallucinationType.DEFENSE
}

class SecurityBehavior extends BehaviorBase {
  public type: BehaviorType.SECURITY = BehaviorType.SECURITY
}

class RefusalBehavior extends BehaviorBase {
  public type: BehaviorType.REFUSAL = BehaviorType.REFUSAL
}

class CustomBehavior extends BehaviorBase {
  public type: BehaviorType.CUSTOM = BehaviorType.CUSTOM
  public biasedDataset: IDataset = $state(new HuggingFaceDataset())
  public unbiasedDataset: IDataset = $state(new HuggingFaceDataset())

  toJSON() {
    return {
      type: this.type,
      biasedDataset: this.biasedDataset,
      unbiasedDataset: this.unbiasedDataset,
    }
  }
}

type BehaviorOptions =
  | AllBias
  | RaceBias
  | NationalityBias
  | GenderBias
  | PhysicalAppearanceBias
  | ReligionBias
  | AgeBias
  | GeneralHallucinationsBehavior
  | MedicalHallucinationsBehavior
  | LegalHallucinationsBehavior
  | DefenseHallucinationsBehavior
  | SecurityBehavior
  | RefusalBehavior
  | CustomBehavior

const getBiasByType = (biasType: BiasType) => {
  switch (biasType) {
    case BiasType.ALL:
      return new AllBias()
    case BiasType.RACE:
      return new RaceBias()
    case BiasType.NATIONALITY:
      return new NationalityBias()
    case BiasType.GENDER:
      return new GenderBias()
    case BiasType.PHYSICAL_APPEARANCE:
      return new PhysicalAppearanceBias()
    case BiasType.RELIGION:
      return new ReligionBias()
    case BiasType.AGE:
      return new AgeBias()
  }
}

const getHallucinationByType = (hallucinationType: HallucinationType) => {
  switch (hallucinationType) {
    case HallucinationType.GENERAL:
      return new GeneralHallucinationsBehavior()
    case HallucinationType.MEDICAL:
      return new MedicalHallucinationsBehavior()
    case HallucinationType.LEGAL:
      return new LegalHallucinationsBehavior()
    case HallucinationType.DEFENSE:
      return new DefenseHallucinationsBehavior()
  }
}
function getBehaviorByType(
  behaviorType: BehaviorType.BIAS,
  options: { biasType: BiasType },
): Extract<BehaviorOptions, { type: BehaviorType.BIAS }>
function getBehaviorByType(
  behaviorType: BehaviorType.HALLUCINATION,
  options: { hallucinationType: HallucinationType },
): Extract<BehaviorOptions, { type: BehaviorType.HALLUCINATION }>
function getBehaviorByType(
  behaviorType: BehaviorType.SECURITY,
): Extract<BehaviorOptions, { type: BehaviorType.SECURITY }>
function getBehaviorByType(
  behaviorType: BehaviorType.REFUSAL,
): Extract<BehaviorOptions, { type: BehaviorType.REFUSAL }>
function getBehaviorByType(
  behaviorType: BehaviorType.CUSTOM,
): Extract<BehaviorOptions, { type: BehaviorType.CUSTOM }>
function getBehaviorByType(
  behaviorType: BehaviorType,
  options?: {
    biasType?: BiasType
    hallucinationType?: HallucinationType
  },
): BehaviorOptions
function getBehaviorByType(
  behaviorType: BehaviorType,
  {
    biasType,
    hallucinationType,
  }: {
    biasType?: BiasType
    hallucinationType?: HallucinationType
  } = {},
) {
  switch (behaviorType) {
    case BehaviorType.BIAS:
      if (!biasType) {
        throw new Error('Bias type is required')
      }
      return getBiasByType(biasType)
    case BehaviorType.HALLUCINATION:
      if (!hallucinationType) {
        throw new Error('Hallucination type is required')
      }
      return getHallucinationByType(hallucinationType)
    case BehaviorType.SECURITY:
      return new SecurityBehavior()
    case BehaviorType.REFUSAL:
      return new RefusalBehavior()
    case BehaviorType.CUSTOM:
      return new CustomBehavior()
  }
}

const biasTypeLabels = {
  [BiasType.ALL]: 'All',
  [BiasType.RACE]: 'Race',
  [BiasType.NATIONALITY]: 'Nationality',
  [BiasType.GENDER]: 'Gender',
  [BiasType.PHYSICAL_APPEARANCE]: 'Physical Appearance',
  [BiasType.RELIGION]: 'Religion',
  [BiasType.AGE]: 'Age',
}

const behaviorTypeLabels = {
  [BehaviorType.BIAS]: 'Bias',
  [BehaviorType.HALLUCINATION]: 'Hallucinations',
  [BehaviorType.SECURITY]: 'Security',
  [BehaviorType.REFUSAL]: 'Refusal',
  [BehaviorType.CUSTOM]: 'Custom',
}

type BiasOptions =
  | RaceBias
  | NationalityBias
  | GenderBias
  | PhysicalAppearanceBias
  | ReligionBias
  | AgeBias

type BehaviorMatcher<TBehavior extends { type: BehaviorType }, TResult> = {
  [BehaviorType.BIAS]: (behavior: Extract<TBehavior, { type: BehaviorType.BIAS }>) => TResult
  [BehaviorType.HALLUCINATION]: (
    behavior: Extract<TBehavior, { type: BehaviorType.HALLUCINATION }>,
  ) => TResult
  [BehaviorType.SECURITY]: (
    behavior: Extract<TBehavior, { type: BehaviorType.SECURITY }>,
  ) => TResult
  [BehaviorType.REFUSAL]: (behavior: Extract<TBehavior, { type: BehaviorType.REFUSAL }>) => TResult
  [BehaviorType.CUSTOM]: (behavior: Extract<TBehavior, { type: BehaviorType.CUSTOM }>) => TResult
}

const matchBehavior = <TBehavior extends { type: BehaviorType }, TResult>(
  behavior: TBehavior,
  matcher: BehaviorMatcher<TBehavior, TResult>,
): TResult => {
  switch (behavior.type) {
    case BehaviorType.BIAS:
      return matcher[BehaviorType.BIAS](behavior as Extract<TBehavior, { type: BehaviorType.BIAS }>)
    case BehaviorType.HALLUCINATION:
      return matcher[BehaviorType.HALLUCINATION](
        behavior as Extract<TBehavior, { type: BehaviorType.HALLUCINATION }>,
      )
    case BehaviorType.SECURITY:
      return matcher[BehaviorType.SECURITY](
        behavior as Extract<TBehavior, { type: BehaviorType.SECURITY }>,
      )
    case BehaviorType.REFUSAL:
      return matcher[BehaviorType.REFUSAL](
        behavior as Extract<TBehavior, { type: BehaviorType.REFUSAL }>,
      )
    case BehaviorType.CUSTOM:
      return matcher[BehaviorType.CUSTOM](
        behavior as Extract<TBehavior, { type: BehaviorType.CUSTOM }>,
      )
  }
}

export const getBehaviorId = (behavior: $state.Snapshot<BehaviorOptions>) => {
  if (behavior.type === BehaviorType.BIAS) {
    return `${behavior.type}-${behavior.biasType}`
  } else if (behavior.type === BehaviorType.HALLUCINATION) {
    return `${behavior.type}-${behavior.hallucinationType}`
  } else if (behavior.type === BehaviorType.SECURITY || behavior.type === BehaviorType.REFUSAL) {
    return `${behavior.type}`
  } else {
    return `${behavior.type}-${getDatasetName(behavior.biasedDataset)}-${getDatasetName(behavior.unbiasedDataset)}`
  }
}

class CustomUtility {
  public dataset?: IDataset = $state(new HuggingFaceDataset())

  constructor(dataset?: IDataset) {
    if (dataset) {
      this.dataset = dataset
    }
  }

  toJSON() {
    return {
      dataset: this.dataset,
    }
  }
}

interface IUnlearningRun {
  id: number
  name: string
  modelId: number
  model: ILlm
  creatorId: number | null
  creatorName: string | null
  targetBehaviors: BehaviorOptions[] | null
  targetUtilities: CustomUtility[] | null
  aggressiveness: number | null
  runId: string
  mlflowRunId: string | null
  status: UnlearningRunStatus
  errorReason: string | null
  approved: boolean
  preProcessProgress: number
  optimizationProgress: number
  postProcessProgress: number
  createdAt: Date
  completedAt: Date | null
  deletedAt: Date | null
}
type IRawUnlearningRun = Omit<IUnlearningRun, 'createdAt' | 'completedAt' | 'deletedAt'> & {
  createdAt: string
  completedAt: string | null
  deletedAt: string | null
}

class HuggingFaceDataset extends ILabelingInfo {
  public type: DatasetMetadataType.HuggingFaceDataset = DatasetMetadataType.HuggingFaceDataset
  public huggingFaceDatasetName: string = $state('')
  public token: string | null = $state(null)
  public tokenId: number | undefined = $state(undefined)
  public saveToken: boolean = $state(false)
  public tokenName: string = $state('')

  constructor(
    huggingFaceDatasetName?: string,
    {
      token = null,
      tokenId = undefined,
      saveToken = false,
      tokenName = '',
    }: {
      token?: string | null
      tokenId?: number
      saveToken?: boolean
      tokenName?: string
    } = {},
  ) {
    super()
    if (huggingFaceDatasetName) {
      this.huggingFaceDatasetName = huggingFaceDatasetName
    }
    this.token = token
    this.tokenId = tokenId
    this.saveToken = saveToken
    this.tokenName = tokenName
  }

  toJSON() {
    return {
      type: this.type,
      huggingFaceDatasetName: this.huggingFaceDatasetName,
      token: this.token,
      tokenId: this.tokenId,
      saveToken: this.saveToken,
      tokenName: this.tokenName,
    }
  }
}

type IDataset = HuggingFaceDataset | HirundoCSV

const getDatasetName = (dataset: $state.Snapshot<IDataset>) => {
  if (dataset?.type === DatasetMetadataType.HuggingFaceDataset) {
    return dataset.huggingFaceDatasetName
  } else if (dataset?.type === DatasetMetadataType.HirundoCSV) {
    return dataset.csvUrl
  }
}

enum DatasetType {
  BIAS = 'bias',
  UNBIAS = 'unbias',
  NORMAL = 'normal',
}

const displayDatasetType = {
  [DatasetType.BIAS]: 'forget',
  [DatasetType.UNBIAS]: 'retain',
  [DatasetType.NORMAL]: 'utility',
}

class UnlearningLlmAdvancedOptions {
  public maxTokensForModel: {
    [DatasetType.BIAS]: number
    [DatasetType.UNBIAS]: number
    [DatasetType.NORMAL]: number
  } | null = $state(null)

  static fromJSON(
    value: ReturnType<UnlearningLlmAdvancedOptions['toJSON']> | null,
  ): UnlearningLlmAdvancedOptions | null {
    if (!value) {
      return null
    }

    const advancedOptions = new UnlearningLlmAdvancedOptions()
    advancedOptions.maxTokensForModel = value.maxTokensForModel
    return advancedOptions
  }

  toJSON() {
    return {
      maxTokensForModel: this.maxTokensForModel,
    }
  }
}
class RunInfo {
  public organizationId: number = $state(-1)

  public targetBehaviors: BehaviorOptions[] = $state([getBiasByType(BiasType.ALL)])
  public targetUtilities: CustomUtility[] = $state([])

  public advancedOptions: UnlearningLlmAdvancedOptions | null = $state(null)
  public aggressiveness: number | null = $state(null)
  constructor(organizationId: number) {
    this.organizationId = organizationId
  }

  toJSON() {
    return {
      organizationId: this.organizationId,
      targetBehaviors: this.targetBehaviors,
      targetUtilities: this.targetUtilities,
      advancedOptions: this.advancedOptions,
      aggressiveness: this.aggressiveness,
    }
  }
}

type HuggingFaceDatasetJson = ReturnType<HuggingFaceDataset['toJSON']>
type ApiHuggingFaceDataset = Omit<HuggingFaceDatasetJson, 'saveToken' | 'tokenName'>
type ApiDataset = ApiHuggingFaceDataset | ReturnType<HirundoCSV['toJSON']>
type RunInfoJson = ReturnType<RunInfo['toJSON']>
type SerializedBehaviorForApi =
  | Pick<Extract<BehaviorOptions, { type: BehaviorType.BIAS }>, 'type' | 'biasType'>
  | Pick<
      Extract<BehaviorOptions, { type: BehaviorType.HALLUCINATION }>,
      'type' | 'hallucinationType'
    >
  | Pick<Extract<BehaviorOptions, { type: BehaviorType.SECURITY }>, 'type'>
  | Pick<Extract<BehaviorOptions, { type: BehaviorType.REFUSAL }>, 'type'>
  | {
      type: BehaviorType.CUSTOM
      biasedDataset: ApiDataset
      unbiasedDataset: ApiDataset
    }

const serializeDatasetForApi = (dataset: IDataset): ApiDataset => {
  if (dataset.type === DatasetMetadataType.HirundoCSV) {
    return dataset.toJSON()
  }

  const { type, huggingFaceDatasetName, token, tokenId } = dataset
  return { type, huggingFaceDatasetName, token, tokenId }
}

const serializeBehaviorForApi = (behavior: BehaviorOptions): SerializedBehaviorForApi => {
  return matchBehavior<BehaviorOptions, SerializedBehaviorForApi>(behavior, {
    [BehaviorType.BIAS]: (matchedBehavior) => ({
      type: matchedBehavior.type,
      biasType: matchedBehavior.biasType,
    }),
    [BehaviorType.HALLUCINATION]: (matchedBehavior) => ({
      type: matchedBehavior.type,
      hallucinationType: matchedBehavior.hallucinationType,
    }),
    [BehaviorType.SECURITY]: (matchedBehavior) => ({ type: matchedBehavior.type }),
    [BehaviorType.REFUSAL]: (matchedBehavior) => ({ type: matchedBehavior.type }),
    [BehaviorType.CUSTOM]: (matchedBehavior) => ({
      type: matchedBehavior.type,
      biasedDataset: serializeDatasetForApi(matchedBehavior.biasedDataset),
      unbiasedDataset: serializeDatasetForApi(matchedBehavior.unbiasedDataset),
    }),
  })
}

const serializeAdvancedOptionsForApi = (advancedOptions: UnlearningLlmAdvancedOptions | null) =>
  advancedOptions ? advancedOptions.toJSON() : null

type ApiRunInfo = Omit<RunInfoJson, 'targetBehaviors' | 'targetUtilities' | 'advancedOptions'> & {
  targetBehaviors: SerializedBehaviorForApi[]
  targetUtilities: Array<{ dataset?: ApiDataset }>
  advancedOptions: ReturnType<typeof serializeAdvancedOptionsForApi>
}

const serializeRunInfoForApi = (runInfo: RunInfo) =>
  ({
    organizationId: runInfo.organizationId,
    targetBehaviors: runInfo.targetBehaviors.map(serializeBehaviorForApi),
    targetUtilities: runInfo.targetUtilities.map(({ dataset }) => ({
      dataset: dataset ? serializeDatasetForApi(dataset) : undefined,
    })),
    advancedOptions: serializeAdvancedOptionsForApi(runInfo.advancedOptions),
    aggressiveness: runInfo.aggressiveness,
  }) satisfies ApiRunInfo

enum UnlearnType {
  BEHAVIOR = 'BEHAVIOR',
  DATA = 'DATA',
}

const unlearnTypeLabels = {
  [UnlearnType.BEHAVIOR]: 'Unlearn behaviors',
  [UnlearnType.DATA]: 'Unlearn data',
}

enum DataTypes {
  PII = 'PII',
}

const dataTypeLabels = {
  [DataTypes.PII]: 'Personally Identifiable Information (PII)',
}

export type { BehaviorOptions, BiasOptions, IDataset, IRawUnlearningRun, IUnlearningRun }
export {
  BehaviorType,
  behaviorTypeLabels,
  behaviorTypeValues,
  Bias,
  BiasType,
  biasTypeLabels,
  biasTypeValues,
  CustomUtility,
  DatasetType,
  dataTypeLabels,
  DataTypes,
  displayDatasetType,
  displayRunStatus,
  displayRunStatusSubtitle,
  getBehaviorByType,
  getBiasByType,
  getDatasetName,
  getRunStatusSubtitle,
  HallucinationsBehavior,
  HallucinationType,
  hallucinationTypeLabels,
  hallucinationTypeValues,
  HuggingFaceDataset,
  matchBehavior,
  RefusalBehavior,
  RunInfo,
  SecurityBehavior,
  serializeRunInfoForApi,
  UnlearningLlmAdvancedOptions,
  UnlearningRunStatus,
  UnlearnType,
  unlearnTypeLabels,
}
